/* KajuGolf - Murmel-Parcours (Station 3, Entdeckungsreise 2.0).

   Top-down mini golf: a marble is flicked (drag + release) through gates of
   flat-lying, arc-shaped kaju stones into a target hollow. Banks are annular
   segments sampled as polylines for circle-vs-segment collision. Own physics,
   no libraries. Rendering/simulation is gated by IntersectionObserver like
   the other stations (see bauprobe2.js for the shared patterns).            */
(function () {
  'use strict';

  var IS_BROWSER = typeof window !== 'undefined' && typeof document !== 'undefined';
  if (!IS_BROWSER) return;

  /* ================= constants ================= */

  var W = 820, H = 640;
  var WALL = 14;                    // playfield frame thickness (acts as wall)
  var BALL_R = 13;
  var REST = 0.75;                  // restitution on banks and walls
  var FRICTION_K = 1.15;            // exponential damping per second
  var STOP_V = 14;                  // px/s below which the marble stops
  var MAX_DRAG = 170;               // drag length cap (px)
  var MAX_SPEED = 840;              // shot speed at full drag (px/s)
  var GRAB_R = 46;                  // pickup radius around the marble
  var SINK_V = 175;                 // max speed to drop into the hole
  var SEG_STEP = 6;                 // degrees per polyline sample
  var SUBSTEPS = 4;                 // anti-tunneling substeps per frame

  var START = { x: 110, y: 552 };
  var HOLE = { x: 600, y: 498, r: 20 };

  var C_PAPER = '#faf7f4', C_LINE = '#532c29', C_BALL = '#0096ae';
  var C_STONES = ['#f1c953', '#f39200', '#6cac53'];

  // Gentle S-course: start bottom-left -> yellow gate up the left side ->
  // orange gate across the upper middle -> green guide arc bends the ball
  // down the right into the hollow, a green catch arc closes the pocket.
  var ARCS = [
    // gate 1 (yellow), vertical passage lower left
    { cx: 60,  cy: 300, ri: 110, ro: 146, a0: -20, a1: 55,  color: '#f1c953' },
    { cx: 430, cy: 420, ri: 140, ro: 176, a0: 140, a1: 215, color: '#f1c953' },
    // gate 2 (orange), horizontal passage upper middle
    { cx: 330, cy: 60,  ri: 100, ro: 136, a0: 35,  a1: 110, color: '#f39200' },
    { cx: 390, cy: 430, ri: 130, ro: 166, a0: 235, a1: 300, color: '#f39200' },
    // final bend (green): guide arc right + catch arc under the hollow
    { cx: 900, cy: 300, ri: 210, ro: 246, a0: 140, a1: 200, color: '#6cac53' },
    { cx: 560, cy: 700, ri: 120, ro: 156, a0: 250, a1: 310, color: '#6cac53' }
  ];

  /* ================= state ================= */

  var canvas, ctx, statusEl, resetBtn;
  var visible = false, lastT = 0;
  var segs = [];                    // sampled bank segments {ax,ay,bx,by}
  var ball = null;                  // {x,y,vx,vy}
  var strokes = 0;
  var phase = 'play';               // 'play' | 'sinking' | 'sunk'
  var drag = null;                  // {dx,dy,len} pointer relative to ball
  var particles = [];
  var sinkT = 0, sinkFrom = null;

  function rad(d) { return d * Math.PI / 180; }

  /* ================= geometry ================= */

  // Sample every arc bank as a closed polyline (outer arc, cap, inner arc,
  // cap) so collision is plain circle-vs-segment everywhere.
  function buildSegments() {
    segs = [];
    ARCS.forEach(function (a) {
      var pts = [], n = Math.max(2, Math.ceil((a.a1 - a.a0) / SEG_STEP)), i, ang;
      for (i = 0; i <= n; i++) {
        ang = rad(a.a0 + (a.a1 - a.a0) * i / n);
        pts.push([a.cx + a.ro * Math.cos(ang), a.cy + a.ro * Math.sin(ang)]);
      }
      for (i = n; i >= 0; i--) {
        ang = rad(a.a0 + (a.a1 - a.a0) * i / n);
        pts.push([a.cx + a.ri * Math.cos(ang), a.cy + a.ri * Math.sin(ang)]);
      }
      for (i = 0; i < pts.length; i++) {
        var p = pts[i], q = pts[(i + 1) % pts.length];
        segs.push({ ax: p[0], ay: p[1], bx: q[0], by: q[1] });
      }
    });
  }

  /* ================= physics ================= */

  function collideSegments() {
    for (var i = 0; i < segs.length; i++) {
      var s = segs[i];
      var abx = s.bx - s.ax, aby = s.by - s.ay;
      var len2 = abx * abx + aby * aby;
      var t = len2 ? ((ball.x - s.ax) * abx + (ball.y - s.ay) * aby) / len2 : 0;
      if (t < 0) t = 0; else if (t > 1) t = 1;
      var cx = s.ax + abx * t, cy = s.ay + aby * t;
      var dx = ball.x - cx, dy = ball.y - cy;
      var d2 = dx * dx + dy * dy;
      if (d2 === 0 || d2 >= BALL_R * BALL_R) continue;
      var d = Math.sqrt(d2), nx = dx / d, ny = dy / d;
      ball.x = cx + nx * BALL_R;
      ball.y = cy + ny * BALL_R;
      var vn = ball.vx * nx + ball.vy * ny;
      if (vn < 0) { ball.vx -= (1 + REST) * vn * nx; ball.vy -= (1 + REST) * vn * ny; }
    }
  }

  function collideWalls() {
    var lo = WALL + BALL_R, hx = W - WALL - BALL_R, hy = H - WALL - BALL_R;
    if (ball.x < lo) { ball.x = lo; if (ball.vx < 0) ball.vx = -ball.vx * REST; }
    if (ball.x > hx) { ball.x = hx; if (ball.vx > 0) ball.vx = -ball.vx * REST; }
    if (ball.y < lo) { ball.y = lo; if (ball.vy < 0) ball.vy = -ball.vy * REST; }
    if (ball.y > hy) { ball.y = hy; if (ball.vy > 0) ball.vy = -ball.vy * REST; }
  }

  function update(dt) {
    var i;
    for (i = particles.length - 1; i >= 0; i--) {
      var p = particles[i];
      p.t += dt;
      if (p.t > p.life) { particles.splice(i, 1); continue; }
      p.vy += 300 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    if (phase === 'sinking') {
      sinkT += dt / 0.45;
      if (sinkT >= 1) { sinkT = 1; phase = 'sunk'; onSunk(); }
      return;
    }
    if (phase !== 'play') return;

    var h = dt / SUBSTEPS;
    for (i = 0; i < SUBSTEPS; i++) {
      if (ball.vx * ball.vx + ball.vy * ball.vy < STOP_V * STOP_V) {
        ball.vx = 0; ball.vy = 0;
        break;
      }
      var damp = Math.exp(-FRICTION_K * h);
      ball.vx *= damp; ball.vy *= damp;
      ball.x += ball.vx * h;
      ball.y += ball.vy * h;
      collideSegments();
      collideWalls();
      var hdx = ball.x - HOLE.x, hdy = ball.y - HOLE.y;
      var slow = ball.vx * ball.vx + ball.vy * ball.vy < SINK_V * SINK_V;
      if (slow && hdx * hdx + hdy * hdy < (HOLE.r - 5) * (HOLE.r - 5)) {
        sinkFrom = { x: ball.x, y: ball.y };
        sinkT = 0;
        phase = 'sinking';
        break;
      }
    }
  }

  /* ================= sound ================= */
  /* PLATZHALTER · echte Sounds folgen */

  var actx = null;
  function tone(freq, at, dur, gain) {
    try {
      if (!actx) actx = new (window.AudioContext || window.webkitAudioContext)();
      if (actx.state === 'suspended') actx.resume();
      var t0 = actx.currentTime + at;
      var o = actx.createOscillator(), g = actx.createGain();
      o.type = 'triangle';
      o.frequency.value = freq;
      g.gain.setValueAtTime(gain, t0);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      o.connect(g); g.connect(actx.destination);
      o.start(t0); o.stop(t0 + dur + 0.02);
    } catch (_) { /* audio is optional */ }
  }
  function soundShot() { tone(190, 0, 0.06, 0.05); }
  function soundSink() { tone(520, 0, 0.09, 0.045); tone(660, 0.09, 0.14, 0.04); }

  /* ================= flow ================= */

  function setStatus(txt) { if (statusEl) statusEl.textContent = txt; }

  function spawnConfetti() {
    for (var i = 0; i < 14; i++) {
      var a = Math.random() * Math.PI * 2, sp = 60 + Math.random() * 130;
      particles.push({
        x: HOLE.x, y: HOLE.y - 6,
        vx: Math.cos(a) * sp, vy: -Math.abs(Math.sin(a)) * sp - 60,
        r: 2.2 + Math.random() * 2, t: 0, life: 1.25,
        color: C_STONES[i % 3]
      });
    }
  }

  function onSunk() {
    setStatus('Drin. ' + strokes + ' Schubser.');
    spawnConfetti();
    soundSink();
    if (window.KajuReise && window.KajuReise.toast) window.KajuReise.toast('Drin – schöner Schubs!');
  }

  function reset() {
    ball = { x: START.x, y: START.y, vx: 0, vy: 0 };
    strokes = 0;
    phase = 'play';
    drag = null;
    particles = [];
    sinkT = 0;
    setStatus('Zieh an der Murmel, ziel durch die Bogen-Tore – und lass los.');
  }

  /* ================= drawing ================= */

  function ringPath(a) {
    ctx.beginPath();
    ctx.arc(a.cx, a.cy, a.ro, rad(a.a0), rad(a.a1));
    ctx.arc(a.cx, a.cy, a.ri, rad(a.a1), rad(a.a0), true);
    ctx.closePath();
  }

  function drawField() {
    ctx.fillStyle = '#efe5da';
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = C_PAPER;
    ctx.fillRect(WALL, WALL, W - 2 * WALL, H - 2 * WALL);
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(83,44,41,.3)';
    ctx.strokeRect(WALL, WALL, W - 2 * WALL, H - 2 * WALL);
    // tee marker
    ctx.save();
    ctx.setLineDash([4, 5]);
    ctx.strokeStyle = 'rgba(83,44,41,.22)';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(START.x, START.y, 19, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }

  function drawHole() {
    ctx.beginPath(); ctx.arc(HOLE.x, HOLE.y, HOLE.r + 5, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(83,44,41,.12)'; ctx.fill();
    ctx.beginPath(); ctx.arc(HOLE.x, HOLE.y, HOLE.r, 0, Math.PI * 2);
    ctx.fillStyle = '#4a2724'; ctx.fill();
    ctx.beginPath(); ctx.arc(HOLE.x + 2, HOLE.y + 2, HOLE.r - 5, 0, Math.PI * 2);
    ctx.fillStyle = '#331a18'; ctx.fill();
    // small flag hint
    ctx.strokeStyle = C_LINE; ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.moveTo(HOLE.x, HOLE.y - 4); ctx.lineTo(HOLE.x, HOLE.y - 46); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(HOLE.x, HOLE.y - 46);
    ctx.lineTo(HOLE.x + 26, HOLE.y - 38);
    ctx.lineTo(HOLE.x, HOLE.y - 30);
    ctx.closePath();
    ctx.fillStyle = '#f39200'; ctx.fill();
  }

  function drawArcs() {
    for (var i = 0; i < ARCS.length; i++) {
      var a = ARCS[i];
      ctx.save(); ctx.translate(4, 4);            // hard block shadow
      ringPath(a); ctx.fillStyle = 'rgba(0,0,0,.28)'; ctx.fill();
      ctx.restore();
      ringPath(a);
      ctx.fillStyle = a.color; ctx.fill();
      ctx.lineWidth = 2; ctx.strokeStyle = C_LINE; ctx.stroke();
    }
  }

  function drawParticles() {
    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      ctx.globalAlpha = Math.max(0, 1 - p.t / p.life);
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = p.color; ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  function drawAim() {
    if (!drag || phase !== 'play') return;
    var len = Math.min(drag.len, MAX_DRAG);
    if (len < 10) return;
    var nx = -drag.dx / drag.len, ny = -drag.dy / drag.len;   // shot direction
    var ex = ball.x + nx * (28 + len * 1.15);
    var ey = ball.y + ny * (28 + len * 1.15);
    ctx.save();
    ctx.strokeStyle = 'rgba(83,44,41,.6)';
    ctx.lineWidth = 2.5;
    ctx.setLineDash([7, 8]);
    ctx.beginPath();
    ctx.moveTo(ball.x + nx * (BALL_R + 4), ball.y + ny * (BALL_R + 4));
    ctx.lineTo(ex, ey);
    ctx.stroke();
    ctx.setLineDash([]);
    var px = -ny, py = nx;                                    // arrow head
    ctx.beginPath();
    ctx.moveTo(ex + nx * 10, ey + ny * 10);
    ctx.lineTo(ex + px * 6, ey + py * 6);
    ctx.lineTo(ex - px * 6, ey - py * 6);
    ctx.closePath();
    ctx.fillStyle = 'rgba(83,44,41,.6)'; ctx.fill();
    // power ring around the marble
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, BALL_R + 6 + 10 * (len / MAX_DRAG), 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(0,150,174,' + (0.25 + 0.45 * len / MAX_DRAG).toFixed(2) + ')';
    ctx.lineWidth = 2; ctx.stroke();
    ctx.restore();
  }

  function drawBall() {
    if (phase === 'sunk') return;
    var x = ball.x, y = ball.y, r = BALL_R;
    if (phase === 'sinking') {
      var e = sinkT * (2 - sinkT);                            // ease-out
      x = sinkFrom.x + (HOLE.x - sinkFrom.x) * e;
      y = sinkFrom.y + (HOLE.y - sinkFrom.y) * e;
      r = BALL_R * (1 - 0.72 * e);
    }
    ctx.beginPath(); ctx.arc(x + 3, y + 3, r, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,.18)'; ctx.fill();
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = C_BALL; ctx.fill();
    ctx.lineWidth = 1.5; ctx.strokeStyle = 'rgba(83,44,41,.5)'; ctx.stroke();
    ctx.beginPath(); ctx.arc(x - r * 0.35, y - r * 0.38, r * 0.3, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,.75)'; ctx.fill();
  }

  function draw() {
    drawField();
    drawHole();
    drawArcs();
    drawParticles();
    drawAim();
    drawBall();
  }

  /* ================= interaction ================= */

  function canvasXY(e) {
    var r = canvas.getBoundingClientRect();
    return [(e.clientX - r.left) * (W / r.width), (e.clientY - r.top) * (H / r.height)];
  }

  function resting() { return ball.vx === 0 && ball.vy === 0; }

  function onDown(e) {
    if (phase !== 'play' || !resting()) return;
    var p = canvasXY(e);
    var dx = p[0] - ball.x, dy = p[1] - ball.y;
    if (dx * dx + dy * dy > GRAB_R * GRAB_R) return;
    drag = { dx: 0, dy: 0, len: 0 };
    try { canvas.setPointerCapture(e.pointerId); } catch (_) { /* synthetic events */ }
  }

  function onMove(e) {
    if (!drag) return;
    var p = canvasXY(e);
    drag.dx = p[0] - ball.x;
    drag.dy = p[1] - ball.y;
    drag.len = Math.sqrt(drag.dx * drag.dx + drag.dy * drag.dy);
  }

  function onUp() {
    if (!drag) return;
    var d = drag;
    drag = null;
    if (d.len < 10 || phase !== 'play' || !resting()) return;
    var sp = Math.min(d.len, MAX_DRAG) / MAX_DRAG * MAX_SPEED;
    ball.vx = -d.dx / d.len * sp;
    ball.vy = -d.dy / d.len * sp;
    strokes++;
    setStatus(strokes + ' Schubser …');
    soundShot();
  }

  /* ================= loop + init ================= */

  function tick(t) {
    requestAnimationFrame(tick);
    if (!visible) { lastT = t; return; }
    var dt = Math.min(0.033, (t - lastT) / 1000 || 0);
    lastT = t;
    update(dt);
    draw();
  }

  function init() {
    canvas = document.getElementById('golf-canvas');
    if (!canvas) return;
    ctx = canvas.getContext('2d');
    statusEl = document.getElementById('golf-status');
    resetBtn = document.getElementById('golf-reset');
    buildSegments();
    reset();
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', function () { drag = null; });
    if (resetBtn) resetBtn.addEventListener('click', reset);
    var io = new IntersectionObserver(function (es) { visible = es[0].isIntersecting; }, { threshold: 0.05 });
    io.observe(canvas);
    requestAnimationFrame(tick);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
